import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  formatLiksDateRangeLabel,
  type LiksDateDayRange,
} from '../../../components/liks/LiksDateRangeDialog/dateRangePicker';
import { useI18n } from '../../../i18n/useI18n';
import type { PageSize } from '../../../types/userConfig';
import { dayToIsoRange } from '../../../utils/dateTimeUtils';
import {
  ALL_STATUS_FILTER,
  ALL_TENANTS_FILTER,
  buildLacWebsiteCrawlsSearchParams,
  CRAWL_JOB_STATUS_FILTERS,
  isCrawlJobStatus,
  LAC_WEBSITE_CRAWLS_SEARCH_DEBOUNCE_MS,
  parseLacWebsiteCrawlsFilterFromSearchParams,
  parseLacWebsiteCrawlsPageFromSearchParams,
  parseLacWebsiteCrawlsPageSizeFromSearchParams,
  tenantOptionLabel,
  type LacWebsiteCrawlsStatusFilter,
} from '../lacWebsiteCrawlsListParams';

type LacTenantOption = {
  name: string;
  displayName?: string | null;
};

type UseLacWebsiteCrawlsSearchParamsOptions = {
  showTenantFilter: boolean;
  tenants: LacTenantOption[] | undefined;
};

export function useLacWebsiteCrawlsSearchParams({
  showTenantFilter,
  tenants,
}: UseLacWebsiteCrawlsSearchParamsOptions) {
  const { locale, translate } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchDebounceTimeoutRef = useRef<number | null>(null);

  const filter = useMemo(
    () =>
      parseLacWebsiteCrawlsFilterFromSearchParams(searchParams, {
        includeTenant: showTenantFilter,
      }),
    [searchParams, showTenantFilter],
  );
  const page = useMemo(
    () => parseLacWebsiteCrawlsPageFromSearchParams(searchParams),
    [searchParams],
  );
  const pageSize = useMemo(
    () => parseLacWebsiteCrawlsPageSizeFromSearchParams(searchParams),
    [searchParams],
  );

  const urlSearchValue = searchParams.get('search') ?? '';
  const [searchInput, setSearchInput] = useState(urlSearchValue);
  const [previousUrlSearchValue, setPreviousUrlSearchValue] =
    useState(urlSearchValue);

  if (urlSearchValue !== previousUrlSearchValue) {
    setPreviousUrlSearchValue(urlSearchValue);
    setSearchInput(urlSearchValue);
  }

  const [isDateDialogOpen, setIsDateDialogOpen] = useState(false);
  const [expandedJobIds, setExpandedJobIds] = useState(() => new Set<string>());

  const filterRef = useRef(filter);
  const pageSizeRef = useRef(pageSize);

  useEffect(() => {
    filterRef.current = filter;
    pageSizeRef.current = pageSize;
  }, [filter, pageSize]);

  useEffect(() => {
    return () => {
      if (searchDebounceTimeoutRef.current !== null) {
        window.clearTimeout(searchDebounceTimeoutRef.current);
      }
    };
  }, []);

  const replaceParams = useCallback(
    ({
      searchInput: nextSearchInput,
      tenant,
      status,
      fromDay,
      toDay,
      page: nextPage,
      pageSize: nextPageSize,
    }: {
      searchInput: string;
      tenant: string;
      status: LacWebsiteCrawlsStatusFilter;
      fromDay: string;
      toDay: string;
      page: number;
      pageSize: PageSize;
    }) => {
      setExpandedJobIds(new Set());
      setSearchParams(
        buildLacWebsiteCrawlsSearchParams({
          searchInput: nextSearchInput,
          tenant,
          status,
          fromDay,
          toDay,
          page: nextPage,
          pageSize: nextPageSize,
          includeTenant: showTenantFilter,
        }),
        { replace: true },
      );
    },
    [setSearchParams, showTenantFilter],
  );

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchInput(value);

      if (searchDebounceTimeoutRef.current !== null) {
        window.clearTimeout(searchDebounceTimeoutRef.current);
        searchDebounceTimeoutRef.current = null;
      }

      const current = filterRef.current;
      const currentPageSize = pageSizeRef.current;

      if (value.trim() === '') {
        replaceParams({
          searchInput: '',
          tenant: current.tenant,
          status: current.status,
          fromDay: current.fromDay,
          toDay: current.toDay,
          page: 1,
          pageSize: currentPageSize,
        });
        return;
      }

      searchDebounceTimeoutRef.current = window.setTimeout(() => {
        replaceParams({
          searchInput: value,
          tenant: filterRef.current.tenant,
          status: filterRef.current.status,
          fromDay: filterRef.current.fromDay,
          toDay: filterRef.current.toDay,
          page: 1,
          pageSize: pageSizeRef.current,
        });
        searchDebounceTimeoutRef.current = null;
      }, LAC_WEBSITE_CRAWLS_SEARCH_DEBOUNCE_MS);
    },
    [replaceParams],
  );

  const handleTenantFilterChange = useCallback(
    (value: string) => {
      replaceParams({
        searchInput,
        tenant: value,
        status: filter.status,
        fromDay: filter.fromDay,
        toDay: filter.toDay,
        page: 1,
        pageSize,
      });
    },
    [
      filter.fromDay,
      filter.status,
      filter.toDay,
      pageSize,
      replaceParams,
      searchInput,
    ],
  );

  const handleStatusFilterChange = useCallback(
    (value: string) => {
      if (value !== ALL_STATUS_FILTER && !isCrawlJobStatus(value)) {
        return;
      }
      replaceParams({
        searchInput,
        tenant: filter.tenant,
        status: value,
        fromDay: filter.fromDay,
        toDay: filter.toDay,
        page: 1,
        pageSize,
      });
    },
    [
      filter.fromDay,
      filter.tenant,
      filter.toDay,
      pageSize,
      replaceParams,
      searchInput,
    ],
  );

  const handleApplyDateRange = useCallback(
    (range: LiksDateDayRange | null) => {
      replaceParams({
        searchInput,
        tenant: filter.tenant,
        status: filter.status,
        fromDay: range?.fromDay ?? '',
        toDay: range?.toDay ?? '',
        page: 1,
        pageSize,
      });
      setIsDateDialogOpen(false);
    },
    [filter.status, filter.tenant, pageSize, replaceParams, searchInput],
  );

  const handlePageChange = useCallback(
    (nextPage: number) => {
      replaceParams({
        searchInput,
        tenant: filter.tenant,
        status: filter.status,
        fromDay: filter.fromDay,
        toDay: filter.toDay,
        page: nextPage,
        pageSize,
      });
    },
    [
      filter.fromDay,
      filter.status,
      filter.tenant,
      filter.toDay,
      pageSize,
      replaceParams,
      searchInput,
    ],
  );

  const handlePageSizeChange = useCallback(
    (nextPageSize: PageSize) => {
      replaceParams({
        searchInput,
        tenant: filter.tenant,
        status: filter.status,
        fromDay: filter.fromDay,
        toDay: filter.toDay,
        page: 1,
        pageSize: nextPageSize,
      });
    },
    [
      filter.fromDay,
      filter.status,
      filter.tenant,
      filter.toDay,
      replaceParams,
      searchInput,
    ],
  );

  const selectedDayRange = useMemo((): LiksDateDayRange | null => {
    if (
      filter.fromDay === '' ||
      filter.toDay === '' ||
      filter.toDay < filter.fromDay
    ) {
      return null;
    }
    return { fromDay: filter.fromDay, toDay: filter.toDay };
  }, [filter.fromDay, filter.toDay]);

  const isoDateRange = useMemo(() => {
    if (selectedDayRange == null) {
      return undefined;
    }
    return dayToIsoRange(selectedDayRange.fromDay, selectedDayRange.toDay);
  }, [selectedDayRange]);

  const tenantFilterOptions = useMemo(() => {
    const sortedTenants = [...(tenants ?? [])].sort((left, right) =>
      tenantOptionLabel(left).localeCompare(tenantOptionLabel(right), locale, {
        sensitivity: 'base',
      }),
    );

    return [
      {
        value: ALL_TENANTS_FILTER,
        label: translate('lac.websiteCrawls.tenantFilter.all'),
      },
      ...sortedTenants.map((tenant) => ({
        value: tenant.name,
        label: tenantOptionLabel(tenant),
      })),
    ];
  }, [locale, tenants, translate]);

  const statusFilterOptions = useMemo(
    () => [
      {
        value: ALL_STATUS_FILTER,
        label: translate('lac.websiteCrawls.statusFilter.all'),
      },
      ...CRAWL_JOB_STATUS_FILTERS.map((status) => ({
        value: status,
        label: translate(`knowledge.crawlStatus.${status}`),
      })),
    ],
    [translate],
  );

  const filteredStatuses =
    filter.status === ALL_STATUS_FILTER ? undefined : [filter.status];

  const rangeLabel = formatLiksDateRangeLabel(
    selectedDayRange,
    locale,
    translate('common.dateRange.entireTime'),
  );

  const hasActiveFilters =
    filter.debouncedStartUrl != null ||
    isoDateRange != null ||
    filter.status !== ALL_STATUS_FILTER ||
    (showTenantFilter && filter.tenant !== ALL_TENANTS_FILTER);

  return {
    searchInput,
    handleSearchChange,
    debouncedStartUrl: filter.debouncedStartUrl,
    tenantFilter: filter.tenant,
    handleTenantFilterChange,
    tenantFilterOptions,
    statusFilter: filter.status,
    handleStatusFilterChange,
    statusFilterOptions,
    filteredStatuses,
    fromDay: filter.fromDay,
    toDay: filter.toDay,
    isDateDialogOpen,
    setIsDateDialogOpen,
    handleApplyDateRange,
    rangeLabel,
    isoDateRange,
    page,
    pageSize,
    handlePageSizeChange,
    handlePageChange,
    expandedJobIds,
    setExpandedJobIds,
    hasActiveFilters,
  };
}
